import { BrowserOptions } from "@sentry/react";
import type { FirebaseOptions } from "firebase/app";

export interface AppConfig {
  name: string;
  production: FirebaseOptions;
  staging: FirebaseOptions;
  productionDomains: string[];
  /** Downloaded players must not restore online auth or initialize network services. */
  offlinePlayback?: boolean;
  isNativePlatform?: boolean;
  getPlatform?: () => string;
  nativeAuthentication?: {
    signInWithGoogle?: NativeSignInFunction;
    signInWithApple?: NativeSignInFunction;
    signOut?: () => Promise<void>;
  };
  nativeAnalytics?: {
    logEvent: (event: string, params?: Record<string, any>) => void;
    setUserId: (userId: string) => void;
    setUserProperty: (key: string, value: string) => void;
    getAppInstanceId: () => Promise<string | undefined>;
  };
  /**
   * Native Remote Config implementation (Capacitor plugin). When provided on a
   * native platform, Remote Config fetches present the ANDROID/iOS app identity
   * instead of the web one — required for Firebase A/B experiments to join
   * variant exposure with native Analytics goal events.
   */
  nativeRemoteConfig?: {
    fetchAndActivate: () => Promise<void>;
    getValue: (
      key: string
    ) => Promise<{ value: string; source: "remote" | "default" | "static" }>;
    setSettings?: (settings: {
      fetchTimeoutInSeconds?: number;
      minimumFetchIntervalInSeconds?: number;
    }) => Promise<void>;
  };
  sentry?: {
    projectId: string;
    recordReplays?: boolean;
    nativeInit?: (
      options: Omit<BrowserOptions, "autoSessionTracking">,
      forwardInit: (options: BrowserOptions) => void
    ) => void;
  };
  updateActivityViewed?: boolean;
  getAttributionData?: () => Record<string, any>;
  remoteConfig?: Record<string, string | number | boolean | undefined>;
}

export type NativeSignInResult = {
  idToken?: string;
  nonce?: string;
  accessToken?: string;
};

export type NativeSignInFunction = () => Promise<NativeSignInResult | null>;

let stored: AppConfig | undefined;

/** Call once at app startup with your {@link AppConfig} before other `@um/ui` Firebase usage. */
export function registerConfig(config: AppConfig) : AppConfig {
  if (stored) {
    console.warn("@um/ui: registerConfig called more than once; ignoring");
    return stored;
  }
  stored = config;
  return config;
}

/**
 * Returns the config registered via {@link registerConfig}.
 * @throws If {@link registerConfig} has not been called yet.
 */
export function currentConfig(): AppConfig {
  if (!stored) {
    throw new Error(
      '@um/ui: registerConfig() must be called first. Import registerConfig from "@um/ui/appConfig" and invoke it from your app entry with your AppConfig.'
    );
  }
  return stored;
}
